/**
 * SignupRequestApprovalDialog Component
 *
 * Dialog for approving or rejecting signup requests.
 * - Approve mode: Shows role dropdown for assigning user role
 * - Reject mode: Shows optional rejection reason textarea
 *
 * Note: Password is auto-generated by backend on approval
 */

import React, { useState, useEffect, useCallback } from 'react';
import { X, Info, AlertTriangle } from 'lucide-react';
import type { SignupRequest } from '../../../types/signup-requests';
import type { AdminRole } from '../../../types/admin-users';
import { adminUsersAPI } from '../../../services/admin-users-api.service';
import { signupRequestsAPI } from '../../../services/signup-requests-api.service';
import ApproveIcon from '../../../assets/icons/approve-icon.svg';
import RejectIcon from '../../../assets/icons/reject-icon.svg';

// CSS for dots loading animation
const dotsLoadingStyles = `
  .dots-loading span {
    animation: dots 1.4s infinite;
    animation-fill-mode: both;
  }
  .dots-loading span:nth-child(2) {
    animation-delay: 0.2s;
  }
  .dots-loading span:nth-child(3) {
    animation-delay: 0.4s;
  }
  @keyframes dots {
    0%, 80%, 100% { opacity: 0; }
    40% { opacity: 1; }
  }
`;

export interface SignupRequestApprovalDialogProps {
  request: SignupRequest | null;
  mode: 'approve' | 'reject';
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onShowToast?: (message: string) => void;
}

export const SignupRequestApprovalDialog: React.FC<SignupRequestApprovalDialogProps> = ({
  request,
  mode,
  isOpen,
  onClose,
  onSuccess,
  onShowToast,
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Approve mode state
  const [roles, setRoles] = useState<AdminRole[]>([]);
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
  const [rolesLoading, setRolesLoading] = useState(false);

  // Reject mode state
  const [rejectionReason, setRejectionReason] = useState('');

  const isApprove = mode === 'approve';

  // Load roles when dialog opens in approve mode
  useEffect(() => {
    if (isOpen && isApprove && roles.length === 0) {
      loadRoles();
    }
  }, [isOpen, isApprove, roles.length]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setError(null);
      setSelectedRoleId(null);
      setRejectionReason('');
    }
  }, [isOpen]);

  const loadRoles = async () => {
    try {
      setRolesLoading(true);
      const rolesResponse = await adminUsersAPI.getRoles();
      setRoles(rolesResponse);
    } catch (err) {
      console.error('[SignupRequestApprovalDialog] Failed to load roles:', err);
      setError('Failed to load roles. Please try again.');
    } finally {
      setRolesLoading(false);
    }
  };

  const handleApprove = useCallback(async () => {
    if (!request || !selectedRoleId) {
      setError('Please select a role for the user.');
      return;
    }

    // Double-check we're not already processing (prevents race conditions)
    if (isProcessing) {
      return;
    }

    try {
      setIsProcessing(true);
      setError(null);

      await signupRequestsAPI.approveSignupRequest({
        request_id: request.id,
        assigned_role_id: selectedRoleId,
      });

      onShowToast?.(`Account successfully created for ${request.profile.name}`);
      onSuccess();
      // Note: Don't call onClose() here - onSuccess already handles closing
    } catch (err: any) {
      console.error('[SignupRequestApprovalDialog] Approve failed:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to approve user.';
      setError(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  }, [request, selectedRoleId, isProcessing, onSuccess, onShowToast]);

  const handleReject = useCallback(async () => {
    if (!request) {
      return;
    }

    // Double-check we're not already processing (prevents race conditions)
    if (isProcessing) {
      return;
    }

    try {
      setIsProcessing(true);
      setError(null);

      await signupRequestsAPI.rejectSignupRequest({
        request_id: request.id,
        rejection_reason: rejectionReason.trim() || undefined,
      });

      onShowToast?.(`Request rejected for ${request.profile.name}`);
      onSuccess();
      // Note: Don't call onClose() here - onSuccess already handles closing
    } catch (err: any) {
      console.error('[SignupRequestApprovalDialog] Reject failed:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to reject user.';
      setError(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  }, [request, rejectionReason, isProcessing, onSuccess, onShowToast]);

  const handleConfirm = useCallback(() => {
    // Guard against multiple clicks while processing
    if (isProcessing) {
      return;
    }
    if (isApprove) {
      handleApprove();
    } else {
      handleReject();
    }
  }, [isApprove, isProcessing, handleApprove, handleReject]);

  if (!isOpen || !request) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Dialog */}
      <div className="relative z-10 w-full max-w-md bg-white rounded-xl shadow-xl p-6">
        {/* Close button */}
        <button
          onClick={onClose}
          disabled={isProcessing}
          className="absolute top-4 right-4 p-1 text-text-secondary hover:text-text-primary rounded-lg hover:bg-background-tertiary transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header with title */}
        <h3 className="text-lg font-semibold text-text-primary">
          {isApprove ? 'Approve User' : 'Reject User'}
        </h3>

        {/* Centered Icon */}
        <div className="flex justify-center my-6">
          {isApprove ? (
            <img src={ApproveIcon} alt="Approve" className="w-24 h-20 object-contain" />
          ) : (
            <img src={RejectIcon} alt="Reject" className="w-20 h-20 object-contain" />
          )}
        </div>

        {/* Main text */}
        <p className="text-center text-text-primary font-medium">
          Are you sure you want to {isApprove ? 'approve' : 'reject'} {request.profile.name}?
        </p>
        <p className="text-center text-sm text-text-secondary mt-2">
          {isApprove
            ? 'Please assign a role for this user before approving their request'
            : 'The user will have to create a fresh account request once rejected'}
        </p>

        {/* Approve Mode: Role Selection */}
        {isApprove && (
          <div className="mt-6">
            {rolesLoading ? (
              <div className="text-sm text-text-secondary text-center">Loading roles...</div>
            ) : (
              <>
                <label className="block text-sm text-text-secondary mb-2">
                  Assign a role from the list:
                </label>
                <select
                  value={selectedRoleId ? String(selectedRoleId) : ''}
                  onChange={(e) => setSelectedRoleId(e.target.value ? Number(e.target.value) : null)}
                  disabled={isProcessing}
                  className="w-full border border-border-default rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
                >
                  <option value="">Select a role</option>
                  {roles.map((role) => (
                    <option key={role.id} value={String(role.id)}>
                      {role.display_name || role.role_name}
                    </option>
                  ))}
                </select>
                <div className="flex items-center gap-1.5 mt-3 text-xs text-text-secondary justify-center">
                  <Info className="w-3.5 h-3.5" />
                  <span>You can change role later through actions later</span>
                </div>
              </>
            )}
          </div>
        )}

        {/* Reject Mode: Rejection Reason */}
        {!isApprove && (
          <div className="mt-6">
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Enter reason for rejection (optional)"
              className="w-full px-3 py-2.5 border border-border-default rounded-lg text-sm text-text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
              rows={3}
              maxLength={500}
              disabled={isProcessing}
            />
            <div className="flex items-center gap-1.5 mt-3 text-xs text-text-secondary justify-center">
              <AlertTriangle className="w-3.5 h-3.5" />
              <span>This action cannot be undone</span>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mt-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        {/* Single action button */}
        <style>{dotsLoadingStyles}</style>
        <button
          style={
            isApprove
              ? selectedRoleId
                ? {
                    backgroundColor: '#0F3340',
                    borderWidth: '1px 1px 4px 1px',
                    borderStyle: 'solid',
                    borderColor: '#021D27',
                    color: 'white',
                  }
                : {
                    backgroundColor: '#E5E7EB',
                    color: '#9CA3AF',
                    cursor: 'not-allowed',
                  }
              : {
                  backgroundColor: '#CA7373',
                  color: 'white',
                }
          }
          className="w-full mt-6 py-2.5 rounded-lg font-medium transition-opacity disabled:opacity-70"
          onClick={handleConfirm}
          disabled={isProcessing || (isApprove && !selectedRoleId)}
        >
          {isProcessing ? (
            <span className="flex items-center justify-center gap-1">
              {isApprove ? 'Approving' : 'Rejecting'}
              <span className="dots-loading">
                <span>.</span>
                <span>.</span>
                <span>.</span>
              </span>
            </span>
          ) : isApprove ? (
            'Approve'
          ) : (
            'Reject'
          )}
        </button>
      </div>
    </div>
  );
};
